import Phaser from "phaser";
import { CANVAS_COLOURS, EMPTY_TILE, cssColour, edgeStripsInView, frameId, sliceGrid, stripChunks, type AssetManifest, type EdgeStrip, type EdgeStrips, type PropInstance, type StripRect } from "daedalus-core";
import { pageTextureKey } from "./atlas.ts";
import { ChunkView } from "./chunk-view.ts";
import type { ChunkPlacement } from "./chunk-placement.ts";

/**
 * The neighbours' edge strips and the border band (P1.6-T144, ADR-109 decision 25), drawn with the camera-stop
 * guide: outside each edge that touches a placed neighbour, that neighbour's ground and props over the one
 * chunk-deep strip `edgeStrips` gives, at reduced strength and read only (nothing here is an interactive
 * object); the border band tinted on the map's own side; a stretch with no neighbour shaded and marked with
 * its word. Chunk textures are made only for the chunks of a strip the view reaches, through a `ChunkView` of
 * this view's own, so a long edge in a close view draws a screenful and a side out of view draws nothing.
 * Props are images cropped to the strip. Redrawn only when the strips, the view, a neighbour's content or the
 * zoom changed, never every frame.
 */

/**
 * How strongly a strip's ground and props are drawn against the map's own, as an opacity.
 */
export const EDGE_STRIP_ALPHA = 0.55;

/**
 * The border band's tint colour: the zones colour, which no part of the guide uses.
 */
export const BORDER_BAND_COLOUR = CANVAS_COLOURS.collisionOpen.rgb;

/**
 * The border band's tint opacity.
 */
export const BORDER_BAND_ALPHA = 0.2;

/**
 * The shade of a stretch with no map, as an opacity of the scrim colour.
 */
export const NO_MAP_ALPHA = 0.45;

/**
 * An outline's width in screen pixels at any zoom.
 */
export const EDGE_STRIP_LINE = 1.5;

/**
 * The visible world rectangle in pixels and the zoom.
 */
export interface StripViewport {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
  readonly zoom: number;
}

/**
 * A ground layer of a neighbour: a terrain or tile layer, its depth in the neighbour's own layer order and a
 * reader of its drawn cells.
 */
export interface StripGround {
  readonly id: string;
  readonly depth: number;
  readonly variant: string | null;
  readonly cells: () => Uint32Array | null;
}

/**
 * A prop layer of a neighbour and its depth.
 */
export interface StripPropLayer {
  readonly id: string;
  readonly depth: number;
  readonly props: readonly PropInstance[];
}

/**
 * What a strip shows of a neighbour: its size, its layers, and a `revision` object that changes identity
 * whenever anything of it changed, so the view redraws exactly then.
 */
export interface StripContent {
  readonly width: number;
  readonly height: number;
  readonly revision: object;
  readonly grounds: readonly StripGround[];
  readonly propLayers: readonly StripPropLayer[];
}

/**
 * The words on the strips: the mark of a stretch with no map, and a neighbour's plaque.
 */
export interface EdgeStripWords {
  readonly noMap: string;
  readonly plaque: (strip: EdgeStrip) => string;
}

/**
 * What the view needs of the world.
 */
export interface EdgeStripOptions {
  readonly tileSize: number;
  readonly chunkSize: number;
  readonly manifest: AssetManifest;
  readonly tilesets: () => readonly string[];
  readonly depth: number;
  readonly words: EdgeStripWords;
  readonly restored: () => void;
}

/**
 * The part of a prop's art that lies in a rectangle, in the image's own pixels after any flip, which is what
 * `setCrop` takes.
 *
 * @param art The art's box in world pixels: its left and top and its size.
 * @param rect The rectangle in world pixels: left, top, right and bottom.
 * @returns The crop, or null when the art lies wholly outside the rectangle.
 */
export function propCrop(art: { readonly left: number; readonly top: number; readonly width: number; readonly height: number }, rect: { readonly left: number; readonly top: number; readonly right: number; readonly bottom: number }): { readonly x: number; readonly y: number; readonly width: number; readonly height: number } | null {
  const x = Math.max(0, rect.left - art.left);
  const y = Math.max(0, rect.top - art.top);
  const width = Math.min(art.width, rect.right - art.left) - x;
  const height = Math.min(art.height, rect.bottom - art.top) - y;
  return width > 0 && height > 0 ? { x, y, width, height } : null;
}

/**
 * One strip's prop images and the content they were made from.
 */
interface StripProps {
  readonly revision: object;
  readonly images: Phaser.GameObjects.Image[];
}

/**
 * The strips of one scene.
 */
export class EdgeStripView {
  private readonly graphics: Phaser.GameObjects.Graphics;
  private readonly chunks: ChunkView;
  private readonly buffer: Uint32Array;
  private readonly attempted = new Map<string, ChunkPlacement>();
  private readonly painted = new Map<string, object>();
  private readonly props = new Map<string, StripProps>();
  private readonly texts = new Map<string, Phaser.GameObjects.Text>();
  private readonly serials = new WeakMap<object, number>();
  private serial = 0;
  private last = "";
  private draws = 0;
  private dirty = false;

  /**
   * @param scene Scene to draw into.
   * @param options The world's sizes, atlas and words, the depth over the map's layers and under the editor's
   *   markers, and what to call when a restored WebGL context needs the strips painted again.
   */
  public constructor(
    private readonly scene: Phaser.Scene,
    private readonly options: EdgeStripOptions,
  ) {
    this.graphics = scene.add.graphics().setDepth(options.depth);
    this.chunks = new ChunkView(scene, options.tileSize, options.chunkSize, null, 0, () => {
      this.painted.clear();
      this.dirty = true;
      options.restored();
    });
    this.buffer = new Uint32Array(options.chunkSize * options.chunkSize);
  }

  /**
   * How many times the strips were redrawn.
   *
   * @returns The count.
   */
  public get redraws(): number {
    return this.draws;
  }

  /**
   * How many chunk textures the strips hold.
   *
   * @returns The count.
   */
  public get textureCount(): number {
    return this.chunks.textureCount;
  }

  /**
   * The chunks the strips hold a texture for, each as `<neighbour>/<layer>@<column>,<row>` in the open map's chunk
   * numbering, in order. A chunk the layer leaves empty holds none.
   *
   * @returns The keys.
   */
  public get chunkKeys(): string[] {
    const keys: string[] = [];
    for (const [key, placement] of this.attempted) {
      if (this.chunks.has(placement)) {
        keys.push(key);
      }
    }
    return keys.sort();
  }

  /**
   * How many prop images the strips hold.
   *
   * @returns The count.
   */
  public get propCount(): number {
    let count = 0;
    for (const entry of this.props.values()) {
      count += entry.images.length;
    }
    return count;
  }

  /**
   * Brings the strips in line with the map's edges and the view, and draws only if anything changed.
   *
   * @param set The strips of the map in view, or null when the guide is off.
   * @param view The visible world rectangle and zoom.
   * @param contentOf A neighbour's content by map id, the same object while it is unchanged, or null.
   * @returns True when a redraw happened.
   */
  public sync(set: EdgeStrips | null, view: StripViewport, contentOf: (mapId: string) => StripContent | null): boolean {
    const tile = this.options.tileSize;
    const tiles: StripRect = { x: view.left / tile, y: view.top / tile, width: view.width / tile, height: view.height / tile };
    const shown = set === null ? null : edgeStripsInView(set, tiles);
    const contents = new Map<string, StripContent | null>();
    for (const strip of shown?.strips ?? []) {
      contents.set(strip.map, contentOf(strip.map));
    }
    const signature = shown === null ? "off" : `${shown.strips.map((s) => `${s.side}:${s.map}:${s.from}:${s.to}:${this.serialOf(contents.get(s.map)?.revision)}`).join(",")}|${shown.noMap.map((n) => `${n.side}:${n.from}:${n.to}`).join(",")}|${Math.floor(view.left)},${Math.floor(view.top)},${Math.ceil(view.width)},${Math.ceil(view.height)},${view.zoom}`;
    if (signature === this.last && !this.dirty) {
      return false;
    }
    this.last = signature;
    this.dirty = false;
    this.draws += 1;
    this.drawGraphics(shown, view);
    this.syncTexts(shown, view);
    this.syncGround(shown, contents, tiles);
    this.syncProps(shown, contents);
    return true;
  }

  /**
   * Removes everything the strips drew from the scene.
   */
  public destroy(): void {
    this.syncGround(null, new Map(), { x: 0, y: 0, width: 0, height: 0 });
    this.syncProps(null, new Map());
    this.syncTexts(null, { left: 0, top: 0, width: 0, height: 0, zoom: 1 });
    this.chunks.clear();
    this.graphics.destroy();
  }

  /**
   * A number that stands for an object while it lives, so a signature can name a neighbour's revision.
   *
   * @param revision The revision object, or undefined.
   * @returns Its number, 0 for none.
   */
  private serialOf(revision: object | undefined): number {
    if (revision === undefined) {
      return 0;
    }
    let number = this.serials.get(revision);
    if (number === undefined) {
      this.serial += 1;
      number = this.serial;
      this.serials.set(revision, number);
    }
    return number;
  }

  /**
   * Draws the band tint, the strips' outlines and the shade of each stretch with no map.
   *
   * @param shown The strips and marks in view, or null for none.
   * @param view The viewport.
   */
  private drawGraphics(shown: EdgeStrips | null, view: StripViewport): void {
    const g = this.graphics;
    g.clear();
    if (shown === null) {
      return;
    }
    const tile = this.options.tileSize;
    const line = EDGE_STRIP_LINE / view.zoom;
    const px = (rect: StripRect): [number, number, number, number] => [rect.x * tile, rect.y * tile, rect.width * tile, rect.height * tile];
    g.fillStyle(BORDER_BAND_COLOUR, BORDER_BAND_ALPHA);
    for (const strip of shown.strips) {
      g.fillRect(...px(strip.band));
    }
    g.fillStyle(CANVAS_COLOURS.entityLabelBackground.rgb, NO_MAP_ALPHA);
    for (const mark of shown.noMap) {
      g.fillRect(...px(mark.outside));
    }
    g.lineStyle(line, CANVAS_COLOURS.cameraStop.rgb, CANVAS_COLOURS.cameraStop.alpha * 0.8);
    for (const rect of [...shown.strips.map((s) => s.outside), ...shown.noMap.map((n) => n.outside)]) {
      g.strokeRect(...px(rect));
    }
  }

  /**
   * Keeps one plaque of text on each strip and each stretch with no map, at a constant size on the screen and
   * at the near corner of the part in view.
   *
   * @param shown The strips and marks in view, or null for none.
   * @param view The viewport.
   */
  private syncTexts(shown: EdgeStrips | null, view: StripViewport): void {
    const wanted = new Map<string, { rect: StripRect; text: string }>();
    for (const strip of shown?.strips ?? []) {
      wanted.set(`strip:${strip.side}:${strip.map}:${strip.from}`, { rect: strip.outside, text: this.options.words.plaque(strip) });
    }
    for (const mark of shown?.noMap ?? []) {
      wanted.set(`none:${mark.side}:${mark.from}`, { rect: mark.outside, text: this.options.words.noMap });
    }
    for (const [key, text] of this.texts) {
      if (!wanted.has(key)) {
        text.destroy();
        this.texts.delete(key);
      }
    }
    const tile = this.options.tileSize;
    const inset = 4 / view.zoom;
    for (const [key, { rect, text }] of wanted) {
      let label = this.texts.get(key);
      if (label === undefined) {
        label = this.scene.add.text(0, 0, text, { fontFamily: "monospace", fontSize: "11px", color: cssColour(CANVAS_COLOURS.entityLabel), backgroundColor: cssColour(CANVAS_COLOURS.entityLabelBackground), padding: { x: 3, y: 2 } }).setOrigin(0, 0).setDepth(this.options.depth + 1);
        this.texts.set(key, label);
      }
      if (label.text !== text) {
        label.setText(text);
      }
      const left = Math.max(rect.x * tile, view.left);
      const top = Math.max(rect.y * tile, view.top);
      label.setScale(1 / view.zoom).setPosition(left + inset, top + inset);
    }
  }

  /**
   * Paints the chunks of each strip that the view reaches and releases every other, repainting all of a
   * neighbour's chunks when its content changed.
   *
   * @param shown The strips in view, or null for none.
   * @param contents The neighbours' content by map id.
   * @param tiles The visible rectangle in tiles.
   */
  private syncGround(shown: EdgeStrips | null, contents: ReadonlyMap<string, StripContent | null>, tiles: StripRect): void {
    const { chunkSize, tileSize, manifest } = this.options;
    const tilesets = this.options.tilesets();
    const wanted = new Set<string>();
    for (const strip of shown?.strips ?? []) {
      const content = contents.get(strip.map);
      if (content === undefined || content === null) {
        continue;
      }
      const stale = this.painted.get(strip.map) !== content.revision;
      for (const chunk of stripChunks(strip, chunkSize, tiles)) {
        for (const ground of content.grounds) {
          const placement: ChunkPlacement = { layerId: `${strip.map}/${ground.id}`, cx: chunk.cx, cy: chunk.cy, depth: ground.depth, band: false };
          const key = `${placement.layerId}@${chunk.cx},${chunk.cy}`;
          wanted.add(key);
          if (this.attempted.has(key) && !stale) {
            continue;
          }
          this.attempted.set(key, placement);
          const cells = ground.cells();
          if (cells === null) {
            continue;
          }
          sliceGrid(cells, this.buffer, EMPTY_TILE, content.width, content.height, chunk.sx, chunk.sy, chunkSize);
          this.chunks.paint(placement, { tiles: this.buffer, chunkSize, tileSize, tilesets, variant: ground.variant, manifest, missing: () => undefined });
          this.chunks.setAlpha(placement, EDGE_STRIP_ALPHA);
        }
      }
      this.painted.set(strip.map, content.revision);
    }
    for (const [key, placement] of this.attempted) {
      if (!wanted.has(key)) {
        this.chunks.release(placement);
        this.attempted.delete(key);
      }
    }
    for (const map of [...this.painted.keys()]) {
      if (!(shown?.strips ?? []).some((s) => s.map === map)) {
        this.painted.delete(map);
      }
    }
  }

  /**
   * Keeps the prop images of each strip: those whose art meets the strip, cropped to it, rebuilt when the
   * neighbour's content changed and removed with the strip.
   *
   * @param shown The strips in view, or null for none.
   * @param contents The neighbours' content by map id.
   */
  private syncProps(shown: EdgeStrips | null, contents: ReadonlyMap<string, StripContent | null>): void {
    const keys = new Set<string>();
    for (const strip of shown?.strips ?? []) {
      const content = contents.get(strip.map);
      if (content === undefined || content === null) {
        continue;
      }
      const key = `${strip.side}:${strip.map}:${strip.from}`;
      keys.add(key);
      const held = this.props.get(key);
      if (held?.revision === content.revision) {
        continue;
      }
      held?.images.forEach((image) => image.destroy());
      this.props.set(key, { revision: content.revision, images: this.propImages(strip, content) });
    }
    for (const [key, entry] of this.props) {
      if (!keys.has(key)) {
        entry.images.forEach((image) => image.destroy());
        this.props.delete(key);
      }
    }
  }

  /**
   * The images of the props of a neighbour whose art meets a strip, moved to where the strip is and cropped
   * to it.
   *
   * @param strip The strip.
   * @param content The neighbour's content.
   * @returns The images.
   */
  private propImages(strip: EdgeStrip, content: StripContent): Phaser.GameObjects.Image[] {
    const { tileSize, manifest } = this.options;
    const dx = strip.outside.x - strip.source.x;
    const dy = strip.outside.y - strip.source.y;
    const rect = { left: strip.outside.x * tileSize, top: strip.outside.y * tileSize, right: (strip.outside.x + strip.outside.width) * tileSize, bottom: (strip.outside.y + strip.outside.height) * tileSize };
    const images: Phaser.GameObjects.Image[] = [];
    for (const layer of content.propLayers) {
      for (const prop of layer.props) {
        const id = frameId(prop.frame.tileset, null, prop.frame.tile);
        const frame = manifest.frames[id];
        if (frame === undefined) {
          continue;
        }
        const left = (prop.x + dx) * tileSize;
        const bottom = (prop.y + dy + 1) * tileSize;
        const crop = propCrop({ left, top: bottom - frame.h, width: frame.w, height: frame.h }, rect);
        if (crop === null) {
          continue;
        }
        const image = this.scene.add.image(left, bottom, pageTextureKey(manifest, frame.page), id).setOrigin(0, 1).setFlip(prop.flipX, prop.flipY).setAlpha(EDGE_STRIP_ALPHA).setDepth(layer.depth + 0.25 + 0.5 * Math.min(1, Math.max(0, (prop.y + 1) / content.height)));
        if (crop.x > 0 || crop.y > 0 || crop.width < frame.w || crop.height < frame.h) {
          image.setCrop(crop.x, crop.y, crop.width, crop.height);
        }
        images.push(image);
      }
    }
    return images;
  }
}
